/**
 * Borrower Conditions Section
 *
 * Modern, consumer-grade conditions display for borrowers.
 * Status-aware cards with left accent bars, large status icons,
 * and prominent upload CTAs. No internal role information shown.
 *
 * Status detection:
 *   - completed: condition is cleared (statusOpen === false)
 *   - under_review: borrower uploaded, pending lender review
 *   - needs_action: borrower needs to upload (or re-upload on failure)
 */

import { CheckCircle2, Circle, Upload, Clock, Download, FileText, AlertCircle, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { SyncStatusBadge } from './ConditionCardShared';
import type { EncompassCondition, BorrowerPairInfo } from '@/types/approval-letter';
import type { ConditionTemplateInfo, ConditionsPortalUpload } from '@/types/conditions-portal';

type BorrowerConditionStatus = 'needs_action' | 'under_review' | 'completed';

interface BorrowerConditionsSectionProps {
  title: string;
  conditions: EncompassCondition[];
  templates: Record<string, ConditionTemplateInfo>;
  uploads: ConditionsPortalUpload[];
  /** Lender notes keyed by condition externalId */
  notes?: Record<string, string>;
  onDownloadTemplate: (externalId: string, templateName: string, borrowerPairId?: string) => void;
  onUploadClick: (condition: EncompassCondition) => void;
  borrowerPairs?: BorrowerPairInfo[];
}

const STATUS_CONFIG: Record<BorrowerConditionStatus, {
  accent: string;
  iconBg: string;
  iconColor: string;
  icon: typeof Upload;
  badgeLabel: string;
  badgeClass: string;
}> = {
  needs_action: {
    accent: 'bg-amber-400',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    icon: Upload,
    badgeLabel: 'Upload Required',
    badgeClass: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200/50'
  },
  under_review: {
    accent: 'bg-blue-400',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    icon: Clock,
    badgeLabel: 'Under Review',
    badgeClass: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200/50'
  },
  completed: {
    accent: 'bg-emerald-400',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    icon: CheckCircle2,
    badgeLabel: 'Completed',
    badgeClass: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/50'
  }
};

function getConditionStatus(
  condition: EncompassCondition,
  conditionUploads: ConditionsPortalUpload[]
): BorrowerConditionStatus {
  // statusOpen is the source of truth for completion
  if (!condition.statusOpen) {
return 'completed';
}

  // Open condition with failed uploads → needs re-upload
  if (conditionUploads.some(u => u.encompassSyncStatus === 'failed')) {
return 'needs_action';
}

  // Open condition with any uploads → under review
  if (conditionUploads.length > 0) {
return 'under_review';
}

  // Open condition with no uploads → needs action
  return 'needs_action';
}

export const BorrowerConditionsSection = ({
  title,
  conditions,
  templates,
  uploads,
  notes,
  onDownloadTemplate,
  onUploadClick,
  borrowerPairs
}: BorrowerConditionsSectionProps) => {
  const openCount = conditions.filter(c => c.statusOpen).length;
  const allCleared = openCount === 0;

  return (
    <div className="space-y-3">
      {/* Section Header — steps-remaining framing */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {allCleared ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          ) : (
            <Circle className="h-5 w-5 text-amber-500" />
          )}
          <h3 className="font-semibold text-slate-900">{title}</h3>
        </div>
        <Badge
          variant="secondary"
          className={allCleared
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-amber-100 text-amber-700'
          }
        >
          {allCleared ? 'All Cleared' : `${openCount} item${openCount !== 1 ? 's' : ''} remaining`}
        </Badge>
      </div>

      {/* Flat list of conditions — status-aware cards */}
      <div className="space-y-3 pl-7">
        {conditions.map((condition) => {
          const conditionUploads = uploads.filter(u => u.conditionExternalId === condition.externalId);
          const status = getConditionStatus(condition, conditionUploads);
          return (
            <BorrowerConditionCard
              key={condition.id}
              condition={condition}
              status={status}
              template={templates[condition.externalId]}
              uploads={conditionUploads}
              note={notes?.[condition.externalId || condition.id || condition.internalId || '']}
              onDownloadTemplate={onDownloadTemplate}
              onUploadClick={onUploadClick}
              borrowerPairs={borrowerPairs}
            />
          );
        })}
      </div>
    </div>
  );
};

interface BorrowerConditionCardProps {
  condition: EncompassCondition;
  status: BorrowerConditionStatus;
  template?: ConditionTemplateInfo;
  uploads: ConditionsPortalUpload[];
  note?: string;
  onDownloadTemplate: (externalId: string, templateName: string, borrowerPairId?: string) => void;
  onUploadClick: (condition: EncompassCondition) => void;
  borrowerPairs?: BorrowerPairInfo[];
}

const BorrowerConditionCard = ({
  condition,
  status,
  template,
  uploads,
  note,
  onDownloadTemplate,
  onUploadClick,
  borrowerPairs
}: BorrowerConditionCardProps) => {
  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;
  const description = condition.externalDescription || condition.internalDescription || condition.title;
  const hasTemplate = template?.hasTemplate;
  const hasUploads = uploads.length > 0;

  return (
    <div
      className={`
        group relative flex items-start gap-4 p-5 rounded-xl transition-all duration-200
        bg-white shadow-sm ring-1 ring-black/[0.04]
        ${status !== 'completed' ? 'hover:shadow-md hover:ring-black/[0.08]' : 'opacity-60'}
      `}
    >
      {/* Left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${config.accent}`} />

      {/* Status icon */}
      <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${config.iconBg}`}>
        <StatusIcon className={`h-5 w-5 ${config.iconColor}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${status === 'completed' ? 'text-slate-500' : 'text-slate-900'}`}>
          {condition.title}
        </p>
        {description !== condition.title && (
          <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">
            {description}
          </p>
        )}

        {/* Note from Lender */}
        {note && (
          <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800 border border-blue-100">
            <span className="font-medium">Note:</span> {note}
          </div>
        )}

        {/* Status badge */}
        <div className="mt-2 flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.badgeClass}`}>
            <StatusIcon className="h-3 w-3" />
            {config.badgeLabel}
          </span>
        </div>

        {/* Template download — per-pair dropdown when multi-pair, subtle link when single-pair */}
        {hasTemplate && status !== 'completed' && borrowerPairs && borrowerPairs.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                <Download className="h-3 w-3" />
                Choose borrower pair
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {borrowerPairs.map((pair) => (
                <DropdownMenuItem
                  key={pair.applicationId || pair.index}
                  onClick={() => onDownloadTemplate(condition.externalId, template?.templateName || 'Template', pair.applicationId)}
                >
                  {pair.displayLabel}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : hasTemplate && status !== 'completed' ? (
          <button
            className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            onClick={() => onDownloadTemplate(condition.externalId, template?.templateName || 'Template')}
          >
            <Download className="h-3 w-3" />
            Download template
          </button>
        ) : null}

        {/* Upload display */}
        {hasUploads && (
          <div className="mt-2 space-y-1">
            {uploads.map((upload) => (
              <div
                key={upload.id}
                className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1"
              >
                <FileText className="h-3 w-3" />
                <span className="truncate flex-1">{upload.originalFilename}</span>
                <SyncStatusBadge status={upload.encompassSyncStatus} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action button — right side */}
      <div className="shrink-0 self-center">
        {status === 'needs_action' && (
          <Button
            size="sm"
            className="bg-[#183C2E] hover:bg-[#1F4D3A] text-white rounded-lg"
            onClick={() => onUploadClick(condition)}
          >
            <Upload className="h-4 w-4 mr-1.5" />
            Upload
          </Button>
        )}
        {status === 'under_review' && (
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
            <Clock className="h-5 w-5 text-blue-400" />
          </div>
        )}
        {status === 'completed' && (
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
          </div>
        )}
      </div>
    </div>
  );
};
